import { Form, Formik } from 'formik';
import React, { useState } from 'react';
import * as Yup from 'yup';

import { PrimaryButton } from '@components/Button/variants';
import Divider from '@components/Divider';
import SwitchField from '@components/Form/SwitchField';
import TokenizeTextField from '@components/Form/TokenizeTextField';
import CustomSpinner, { CustomSpinnerColor } from '@components/Spinner/CustomSpinner';
import { useGroupSubscriptionConfirm, useTeamSubscriptionsCreate } from '@hooks';
import { Theme } from '@interfaces/global';
import { ACCESS_ANNUAL, ACCESS_PRO, GROUP_SUBSCRIPTION_TEAM } from '@src/constants';
import { AutocompleteOption } from '@utils/interfaces';

import subscriptionEmailsValidation from '../../subscriptionEmailsValidation';
import SettingHeaderSeperator from '../SettingHeaderSeperator';
import styles from './styles.module.scss';

const validationSchema = () =>
  Yup.object().shape({
    emails: subscriptionEmailsValidation(),
  });

const INITIAL_VALUES = { emails: [], overrideAsPro: false };
const OVERRIDE_AS_PRO = 'overrideAsPro';

export default function AddTeamMembers() {
  const [totalCost, setTotalCost] = useState(0);
  const [totalEligibleSub, setTotalEligibleSubs] = useState(0);

  const { addTeamSubscriptionsAsync } = useTeamSubscriptionsCreate();
  const { mutateAsync: confirmGroupSubscription, isLoading: isLoadingConfirmGroupSubscription } =
    useGroupSubscriptionConfirm();

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    const accessLevel = values.overrideAsPro ? ACCESS_PRO : ACCESS_ANNUAL;
    const resp = await addTeamSubscriptionsAsync({
      emails: values.emails.map((email) => email.value),
      access_level: accessLevel,
    });
    if (resp?.success) {
      resetForm();
    }
    setSubmitting(false);
  };

  const prepareConfirmation = async ({
    emails,
    overrideAsPro,
  }: {
    emails: AutocompleteOption[];
    overrideAsPro: boolean;
  }) => {
    const teamData = await confirmGroupSubscription({
      emails: emails.map((email) => email.value),
      group_subscription_type: GROUP_SUBSCRIPTION_TEAM,
      access_level: overrideAsPro ? ACCESS_PRO : ACCESS_ANNUAL,
    });
    setTotalCost(teamData.totalPrice);
    setTotalEligibleSubs(teamData.totalEligibleSubs);
  };

  return (
    <Formik
      onSubmit={handleSubmit}
      initialValues={INITIAL_VALUES}
      validationSchema={validationSchema}
    >
      {({ isSubmitting, isValid, dirty, values }) => (
        <Form data-test="add-to-team-form">
          <SettingHeaderSeperator sectionName="Invite team members by email" />
          <TokenizeTextField
            className="!mt-0 mb-24"
            name="emails"
            placeholder="Enter emails"
            label="Add people by email, separating each address by a comma."
            onChangeCallBack={() => prepareConfirmation(values)}
          />

          {totalEligibleSub > 0 && isValid && dirty && (
            <>
              <Divider marginY="medium" />
              <div className={styles.totalContainer}>
                <div>Accounts total: {totalEligibleSub}</div>
                {isLoadingConfirmGroupSubscription ? (
                  <CustomSpinner color={CustomSpinnerColor.PrimaryMagenta} />
                ) : (
                  <div>${totalCost / 100}.00</div>
                )}
              </div>
              <Divider marginY="medium" />
            </>
          )}
          <SwitchField
            className="mb-24"
            colorTheme={Theme.Light}
            id={OVERRIDE_AS_PRO}
            name={OVERRIDE_AS_PRO}
            label="Add as &ldquo;pro&rdquo; account"
            onChangeCallBack={() => prepareConfirmation(values)}
          />
          <PrimaryButton
            as="button"
            type="submit"
            disabled={isSubmitting}
            isLoading={isSubmitting}
            fullWidth
          >
            Add to team
          </PrimaryButton>
        </Form>
      )}
    </Formik>
  );
}
